import { useEffect, useState } from "react";
import { Phone, Menu, X } from "lucide-react";
import { Logo } from "@/components/landing/Logo";
import { useHashNav } from "@/utils/useHashNav";

const NAV_LINKS = [
  { label: "Services", href: "#services" },
  { label: "About", href: "#about" },
  { label: "Gallery", href: "#gallery" },
  { label: "Reviews", href: "#testimonials" },
  { label: "Contact", href: "#quote" },
];

export const Header = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const nav = useHashNav();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-header"
      className={`sticky top-0 z-50 backdrop-blur-xl bg-white/80 border-b border-zinc-200 transition-shadow duration-300 ${
        scrolled ? "shadow-[0_4px_24px_rgba(0,0,0,0.08)]" : ""
      }`}
    >
      <div className="container-x flex items-center justify-between h-24">
        <a href={nav.href("#top")} onClick={(e) => nav.go(e, "#top")} data-testid="header-logo-link" className="flex items-center">
          <Logo dark />
        </a>

        <nav className="hidden lg:flex items-center gap-8" data-testid="desktop-nav">
          {NAV_LINKS.map((link) => (
            <a
              key={link.href}
              href={nav.href(link.href)}
              onClick={(e) => nav.go(e, link.href)}
              data-testid={`nav-link-${link.label.toLowerCase()}`}
              className="text-sm font-medium text-zinc-600 hover:text-zinc-900 transition-colors"
            >
              {link.label}
            </a>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-4">
          <a
            href="tel:+15068714144"
            data-testid="header-phone-link"
            className="flex items-center gap-2 text-sm font-semibold text-zinc-900 hover:text-brand-primary transition-colors"
          >
            <Phone className="w-4 h-4" /> (506) 871-4144
          </a>
          <a
            href={nav.href("#quote")}
            onClick={(e) => nav.go(e, "#quote")}
            data-testid="header-quote-btn"
            className="px-5 py-2.5 rounded-md bg-brand-primary bg-brand-primary-hover text-black text-sm font-semibold transition-all duration-200 hover:-translate-y-0.5 hover:shadow-lg hover:shadow-[#D4AF37]/20"
          >
            Get a Free Quote
          </a>
        </div>

        <button
          data-testid="mobile-menu-btn"
          className="lg:hidden p-2 text-zinc-900"
          onClick={() => setOpen(!open)}
          aria-label="Toggle menu"
        >
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden border-t border-zinc-200 bg-white/95 backdrop-blur-xl" data-testid="mobile-nav">
          <nav className="px-6 py-6 flex flex-col gap-4">
            {NAV_LINKS.map((link) => (
              <a
                key={link.href}
                href={nav.href(link.href)}
                onClick={(e) => nav.go(e, link.href)}
                data-testid={`mobile-nav-link-${link.label.toLowerCase()}`}
                className="text-base font-medium text-zinc-700"
                onClickCapture={() => setOpen(false)}
              >
                {link.label}
              </a>
            ))}
            <a
              href="tel:+15068714144"
              data-testid="mobile-phone-link"
              className="flex items-center gap-2 text-base font-semibold text-zinc-900"
              onClick={() => setOpen(false)}
            >
              <Phone className="w-4 h-4" /> (506) 871-4144
            </a>
            <a
              href={nav.href("#quote")}
              onClick={(e) => {
                nav.go(e, "#quote");
                setOpen(false);
              }}
              data-testid="mobile-quote-btn"
              className="mt-2 px-5 py-3 rounded-md bg-brand-primary text-black text-sm font-semibold text-center"
            >
              Get a Free Quote
            </a>
          </nav>
        </div>
      )}
    </header>
  );
};
